"use client"

import type { MultiPayload } from "@defuse-protocol/contract-types"
import {
  type AuthMethod,
  authIdentity,
  prepareBroadcastRequest,
  utils,
} from "@defuse-protocol/internal-utils"
import { createVerificationMessage } from "@src/components/DefuseSDK/core/messages"
import { extractWalletErrorCode } from "@src/components/DefuseSDK/utils/walletErrorExtractor"
import {
  authenticateAndStoreTokens,
  clearActiveSession,
  validateTokenForWallet,
} from "@src/features/auth/actions/auth"
import {
  clearAuthJourneyLease,
  completeAuthJourney,
  failAuthJourney,
} from "@src/features/auth/analytics/authJourneyLease"
import { appSessionMachine } from "@src/features/auth/machines/appSessionMachine"
import {
  type AuthMachineRef,
  authMachine,
} from "@src/features/auth/machines/authMachine"
import {
  type VerificationResult,
  walletVerificationMachine,
} from "@src/features/auth/machines/walletVerificationMachine"
import { usePathnameViewTracking } from "@src/hooks/usePathnameViewTracking"
import { useWalletAgnosticSignMessage } from "@src/hooks/useWalletAgnosticSignMessage"
import { syncWalletAuthCache } from "@src/hooks/useWalletAuth"
import { useWalletSnapshot } from "@src/hooks/useWalletSnapshot"
import { appBootstrapMachine } from "@src/machines/appBootstrapMachine"
import { appRouterMachine } from "@src/machines/appRouterMachine"
import { useBypassedWalletsStore } from "@src/stores/useBypassedWalletsStore"
import { WalletConnectionFailure } from "@src/utils/classifyWalletConnectionFailure"
import { logger } from "@src/utils/logger"
import { useQueryClient } from "@tanstack/react-query"
import { useActorRef, useSelector } from "@xstate/react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
} from "react"
import { type ActorRefFrom, fromPromise, type SnapshotFrom } from "xstate"

type BootstrapActorRef = ActorRefFrom<typeof appBootstrapMachine>
const BootstrapContext = createContext<BootstrapActorRef | null>(null)

/** Returns the bootstrap machine actor ref for sending events */
export function useBootstrapActor(): BootstrapActorRef {
  const actor = useContext(BootstrapContext)
  if (!actor) {
    throw new Error(
      "useBootstrapActor must be used within AppBootstrapProvider"
    )
  }
  return actor
}

/** Returns a selected slice of bootstrap machine state */
export function useBootstrapSelector<T>(
  selector: (snapshot: SnapshotFrom<typeof appBootstrapMachine>) => T
): T {
  const actor = useBootstrapActor()
  return useSelector(actor, selector)
}

/** Returns the current machine state value string */
export function useBootstrapState(): string {
  return useBootstrapSelector((snap) => {
    if (snap.hasTag("initializing")) return "initializing"
    if (snap.hasTag("session_unreachable")) return "session_unreachable"
    if (snap.hasTag("signed_in")) return "signed_in"
    if (snap.hasTag("verifying")) return "verifying"
    if (snap.hasTag("signed_out")) return "signed_out"
    return "unknown"
  })
}

/** Returns the raw XState state value for bootstrap internals/debugging. */
export function useBootstrapRawState(): SnapshotFrom<
  typeof appBootstrapMachine
>["value"] {
  return useBootstrapSelector((snap) => snap.value)
}

/**
 * Returns the persistent authMachine child actor from bootstrap.
 * Safe to call anywhere inside AppBootstrapProvider.
 */
export function useAuthActor(): AuthMachineRef {
  const actor = useBootstrapActor()
  return useSelector(actor, (snap) => snap.children.authActor as AuthMachineRef)
}

export function AppBootstrapProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  usePathnameViewTracking(pathname)
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()

  const { walletState, restoreStatus } = useWalletSnapshot()
  const signMessage = useWalletAgnosticSignMessage()
  const isWalletBypassed = useBypassedWalletsStore(
    (state) => state.isWalletBypassed
  )

  const walletStateRef = useRef(walletState)
  walletStateRef.current = walletState
  const signMessageRef = useRef(signMessage)
  signMessageRef.current = signMessage
  const isWalletBypassedRef = useRef(isWalletBypassed)
  isWalletBypassedRef.current = isWalletBypassed

  const actorRef = useActorRef(
    appBootstrapMachine.provide({
      actions: {
        refreshRouter: () => {
          router.refresh()
        },
        clearSession: () => {
          void clearActiveSession()
        },
        completeAuthJourney: ({ context }) => {
          if (context.sessionResult?.status !== "authorized") return
          completeAuthJourney(context.chainType, context.address)
        },
        clearAuthJourneyLease,
      },
      actors: {
        appAuth: authMachine.provide({
          actions: {
            clearAuthJourneyLease,
            failAuthJourney: ({ context }, failure) =>
              failAuthJourney(context.chainType, failure),
          },
          actors: {
            appWalletVerification: walletVerificationMachine.provide({
              actions: {
                logError: (_, { error }: { error: unknown }) => {
                  const errorCode = extractWalletErrorCode(error, null)
                  const failure = new WalletConnectionFailure(error, errorCode)
                  if (failure.isActionable) {
                    logger.error("Wallet verification failed", {
                      error: failure,
                      chain: walletStateRef.current.chainType ?? "unknown",
                    })
                  }
                },
              },
              actors: {
                verifyWallet: fromPromise(
                  async ({ signal }): Promise<VerificationResult> => {
                    const ws = walletStateRef.current
                    if (ws.address == null || ws.chainType == null) {
                      return { success: false }
                    }

                    const intentsUserId =
                      authIdentity.authHandleToIntentsUserId(
                        ws.address,
                        ws.chainType
                      )
                    const walletMessage = await createVerificationMessage(
                      { signerId: intentsUserId },
                      ws.chainType
                    )
                    const walletSignature =
                      await signMessageRef.current(walletMessage)

                    if (signal.aborted) {
                      return { success: false }
                    }

                    const signedData =
                      prepareBroadcastRequest.prepareSwapSignedData(
                        walletSignature,
                        {
                          userAddress: ws.address,
                          userChainType: ws.chainType,
                        }
                      )

                    const result = await authenticateAndStoreTokens({
                      signedData,
                      address: ws.address,
                      authMethod: ws.chainType,
                    })

                    if (result.success) {
                      return { success: true, expiresAt: result.expiresAt }
                    }

                    if (
                      walletSignature.type === "NEP413" &&
                      !utils.isImplicitAccount(
                        walletSignature.signatureData.accountId
                      )
                    ) {
                      return {
                        success: false,
                        failedNearSignature: {
                          accountId: walletSignature.signatureData.accountId,
                          publicKey: walletSignature.signatureData.publicKey,
                          signedData,
                        },
                      }
                    }

                    return { success: false }
                  }
                ),
              },
            }),
            banService: fromPromise(
              async ({
                input,
                signal,
              }: {
                input: { address: string; chainType: string }
                signal: AbortSignal
              }): Promise<{ isBanned: boolean }> => {
                const response = await fetch(
                  `/api/account/validate-banned?address=${encodeURIComponent(input.address)}&chainType=${encodeURIComponent(input.chainType)}`,
                  { signal }
                )
                if (!response.ok) {
                  throw new Error("Failed to validate banned account")
                }
                const data = (await response.json()) as { isBanned: boolean }
                return { isBanned: data.isBanned }
              }
            ),
            safetyService: fromPromise(
              async ({
                input,
                signal,
              }: {
                input: { address: string; chainType: string }
                signal: AbortSignal
              }): Promise<{ status: "safe" | "unsafe" }> => {
                if (isWalletBypassedRef.current(input.address)) {
                  return { status: "safe" }
                }
                if (input.chainType !== "evm") {
                  return { status: "safe" }
                }
                const response = await fetch(
                  `/api/addresses/${input.address}/safety`,
                  { signal }
                )
                if (!response.ok) {
                  throw new Error("Failed to check safety status")
                }
                const data = (await response.json()) as {
                  safetyStatus: "safe" | "unsafe"
                }
                return { status: data.safetyStatus }
              }
            ),
            reauthAfterPubkey: fromPromise(
              async ({
                input,
                signal,
              }: {
                input: {
                  signedData: MultiPayload
                  address: string
                  chainType: AuthMethod
                }
                signal: AbortSignal
              }): Promise<{ success: boolean }> => {
                if (signal.aborted) return { success: false }
                const result = await authenticateAndStoreTokens({
                  signedData: input.signedData,
                  address: input.address,
                  authMethod: input.chainType,
                })
                return { success: result.success }
              }
            ),
          },
        }),
        appRouter: appRouterMachine.provide({
          actions: {
            navigate: (_, { url }: { url: string }) => {
              logger.trace("[bootstrap] nav →", { url })
              router.replace(url)
            },
          },
        }),
        appSession: appSessionMachine.provide({
          actors: {
            checkSession: fromPromise(
              async ({
                input,
              }: {
                input: { address: string; chainType: string }
              }) => {
                return validateTokenForWallet(input.address, input.chainType)
              }
            ),
          },
        }),
      },
    }),
    { input: { pathname } }
  )
  const authRef = useSelector(
    actorRef,
    (snap) => snap.children.authActor as AuthMachineRef
  )
  const bootstrapAddress = useSelector(authRef, (snap) => snap.context.address)
  const bootstrapChainType = useSelector(
    authRef,
    (snap) => snap.context.chainType
  )
  const bootstrapSessionResult = useSelector(
    actorRef,
    (snap) => snap.context.sessionResult
  )
  const isPublicRouteIdle = useSelector(actorRef, (snap) =>
    snap.matches({ signed_out: "public_route_idle" })
  )

  // ==========================================================================
  // Effect A: Wallet state bridge — machine detects transitions via guards
  // ==========================================================================
  useEffect(() => {
    actorRef.send({
      type: "WALLET_STATE",
      address: walletState.address ?? null,
      chainType: walletState.chainType ?? null,
      restoreStatus,
    })
  }, [walletState.address, walletState.chainType, restoreStatus, actorRef])

  // Re-emit when landing on public_route_idle so a wallet switch dropped during
  // navigating_to_login (isConnect-only there while SIGN_OUT keeps address) is
  // replayed once the redirect finishes.
  useEffect(() => {
    if (!isPublicRouteIdle) return
    actorRef.send({
      type: "WALLET_STATE",
      address: walletState.address ?? null,
      chainType: walletState.chainType ?? null,
      restoreStatus,
    })
  }, [
    isPublicRouteIdle,
    walletState.address,
    walletState.chainType,
    restoreStatus,
    actorRef,
  ])

  // ==========================================================================
  // Effect B: Pathname bridge
  // ==========================================================================
  useEffect(() => {
    actorRef.send({
      type: "PATHNAME_CHANGED",
      pathname,
      searchRedirect: searchParams.get("redirect"),
    })
  }, [pathname, searchParams, actorRef])

  // ==========================================================================
  // Effect C: Keep legacy token_validation cache in sync with bootstrap auth.
  // Some UI still reads useWalletAuth/useConnectWallet, so without this bridge
  // it can stay stuck on a fresh "unauthenticated" result after verification.
  // ==========================================================================
  useEffect(() => {
    syncWalletAuthCache(
      queryClient,
      bootstrapAddress,
      bootstrapChainType,
      bootstrapSessionResult
    )
  }, [
    queryClient,
    bootstrapAddress,
    bootstrapChainType,
    bootstrapSessionResult,
  ])

  return (
    <BootstrapContext.Provider value={actorRef}>
      {children}
    </BootstrapContext.Provider>
  )
}
